import dayjs from 'dayjs';
import { OrderCancellationReason } from '@/models/orders.model';
import {
  ICancelledOrdersData,
  ICancelledOrdersQuery,
  ICancelledPartnerItem,
  ICancelledReasonItem,
  IGetCancelledPartnersResponse,
  IGetCancelledReasonsResponse,
} from '@/models/cancelledOrders.model';
import { sortCancelledPartnersByShare } from './sortPartners';

const buildSeries = (from: dayjs.Dayjs, to: dayjs.Dayjs) => {
  const days = to.diff(from, 'day') + 1;
  if (days <= 0) return [];

  return Array.from({ length: days }, (_, index) => {
    const date = from.add(index, 'day');
    const confirmedCount = 80 + (index % 5) * 8;
    const cancelledCount = 4 + (index % 4);
    const total = confirmedCount + cancelledCount;

    return {
      date: date.format('YYYY-MM-DD'),
      confirmed_amount: confirmedCount * 1100,
      cancelled_amount: cancelledCount * 1050,
      confirmed_count: confirmedCount,
      cancelled_count: cancelledCount,
      confirmed_share_percent: Number(((confirmedCount / total) * 100).toFixed(1)),
      cancelled_share_percent: Number(((cancelledCount / total) * 100).toFixed(1)),
    };
  });
};

export const getMockCancelledOrdersData = (query: ICancelledOrdersQuery = {}): ICancelledOrdersData => {
  const today = dayjs().startOf('day');
  const rangeFrom = query.date_from
    ? dayjs(query.date_from).startOf('day')
    : today.subtract(11, 'month').startOf('month');
  let rangeTo = query.date_to ? dayjs(query.date_to).startOf('day') : today;
  if (rangeTo.isAfter(today)) rangeTo = today;

  const series = buildSeries(rangeFrom, rangeTo);
  const cancelledCount = series.reduce((sum, item) => sum + item.cancelled_count, 0);
  const confirmedCount = series.reduce((sum, item) => sum + item.confirmed_count, 0);
  const cancelledAmount = series.reduce((sum, item) => sum + item.cancelled_amount, 0);
  const confirmedAmount = series.reduce((sum, item) => sum + item.confirmed_amount, 0);

  return {
    period: {
      from: rangeFrom.format('YYYY-MM-DD'),
      to: rangeTo.format('YYYY-MM-DD'),
    },
    previous_period: {
      from: rangeFrom.subtract(Math.max(series.length, 1), 'day').format('YYYY-MM-DD'),
      to: rangeFrom.subtract(1, 'day').format('YYYY-MM-DD'),
    },
    kpis: {
      cancelled_count: { value: cancelledCount, change_percent: 9.2 },
      cancelled_amount: { value: cancelledAmount, change_percent: 7.7 },
      cancelled_avg_check: {
        value: Number((cancelledAmount / Math.max(cancelledCount, 1)).toFixed(2)),
        change_percent: 1.4,
      },
      cancelled_share_percent: {
        value: Number(((cancelledCount / Math.max(cancelledCount + confirmedCount, 1)) * 100).toFixed(1)),
        change_pp: -0.6,
      },
    },
    series,
    totals: {
      confirmed_amount: confirmedAmount,
      cancelled_amount: cancelledAmount,
      confirmed_count: confirmedCount,
      cancelled_count: cancelledCount,
      cancelled_share_percent: Number(
        ((cancelledCount / Math.max(cancelledCount + confirmedCount, 1)) * 100).toFixed(1),
      ),
    },
    reasons: [
      {
        code: OrderCancellationReason.PRODUCT_UNAVAILABLE_SUPPLIER,
        label: null,
        share_percent: 27.4,
        change_percent: 3.1,
        count: 65,
      },
      {
        code: OrderCancellationReason.CLIENT_NO_CONTACT_3_DAYS,
        label: null,
        share_percent: 22.1,
        change_percent: -1.4,
        count: 52,
      },
      {
        code: OrderCancellationReason.SUPPLIER_SHIPMENT_OVERDUE,
        label: null,
        share_percent: 14.8,
        change_percent: 2.2,
        count: 35,
      },
      {
        code: OrderCancellationReason.CLIENT_REFUSED_NO_REASON,
        label: null,
        share_percent: 11.2,
        change_percent: -0.8,
        count: 27,
      },
      {
        code: OrderCancellationReason.RECIPIENT_REFUSED_NOVA_POSHTA,
        label: null,
        share_percent: 8.6,
        change_percent: 1.1,
        count: 20,
      },
      {
        code: OrderCancellationReason.PRODUCT_MISMATCH_SPECS,
        label: null,
        share_percent: 5.9,
        change_percent: -2.3,
        count: 14,
      },
      {
        code: OrderCancellationReason.ONLINE_PAYMENT_ERROR,
        label: null,
        share_percent: 4.1,
        change_percent: 0.4,
        count: 10,
      },
      {
        code: OrderCancellationReason.NOVA_POSHTA_DELIVERY_EXPENSIVE,
        label: null,
        share_percent: 3.2,
        change_percent: null,
        count: 8,
      },
      {
        code: OrderCancellationReason.TEST_OR_DUPLICATE_ORDER,
        label: null,
        share_percent: 1.8,
        change_percent: 0,
        count: 4,
      },
      {
        code: OrderCancellationReason.OTHER,
        label: null,
        share_percent: 0.9,
        change_percent: -0.2,
        count: 2,
      },
    ],
    partners: [
      {
        id: '1',
        name: 'ТОВ Альфа Постач',
        cancelled_share_percent: 18.2,
        change_percent: 2.1,
        cancelled_count: 41,
      },
      {
        id: '2',
        name: 'ФОП Петренко',
        cancelled_share_percent: 14.5,
        change_percent: -1.3,
        cancelled_count: 33,
      },
      {
        id: '3',
        name: 'ТОВ Бета Трейд',
        cancelled_share_percent: 11.8,
        change_percent: 0.7,
        cancelled_count: 27,
      },
      {
        id: '4',
        name: 'ТОВ Гамма Логістик',
        cancelled_share_percent: 9.4,
        change_percent: null,
        cancelled_count: 21,
      },
      {
        id: '5',
        name: 'ТОВ Дельта Маркет',
        cancelled_share_percent: 7.6,
        change_percent: -0.5,
        cancelled_count: 17,
      },
      {
        id: '6',
        name: 'ФОП Коваленко',
        cancelled_share_percent: 6.2,
        change_percent: 1.1,
        cancelled_count: 14,
      },
      {
        id: '7',
        name: 'ТОВ Епсилон',
        cancelled_share_percent: 5.1,
        change_percent: 0,
        cancelled_count: 11,
      },
      {
        id: '8',
        name: 'ТОВ Дзета',
        cancelled_share_percent: 4.3,
        change_percent: -2.0,
        cancelled_count: 10,
      },
      {
        id: '9',
        name: 'ТОВ Ета Сервіс',
        cancelled_share_percent: 3.7,
        change_percent: 0.3,
        cancelled_count: 8,
      },
      {
        id: '10',
        name: 'ТОВ Тета',
        cancelled_share_percent: 2.9,
        change_percent: 1.4,
        cancelled_count: 6,
      },
    ],
  };
};

export const getMockCancelledReasonsResponse = (page = 1, perPage = 20): IGetCancelledReasonsResponse => {
  const all = getMockCancelledOrdersData().reasons;
  const start = (page - 1) * perPage;
  const data = all.slice(start, start + perPage) as ICancelledReasonItem[];
  return {
    data,
    meta: {
      current_page: page,
      last_page: Math.max(1, Math.ceil(all.length / perPage)),
      per_page: perPage,
      total: all.length,
    },
  };
};

export const getMockCancelledPartnersResponse = (page = 1, perPage = 20): IGetCancelledPartnersResponse => {
  const all = sortCancelledPartnersByShare(getMockCancelledOrdersData().partners);
  const start = (page - 1) * perPage;
  const data = all.slice(start, start + perPage) as ICancelledPartnerItem[];
  return {
    data,
    meta: {
      current_page: page,
      last_page: Math.max(1, Math.ceil(all.length / perPage)),
      per_page: perPage,
      total: all.length,
    },
  };
};
